import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";

// Credential field codes that hold the provider's own profession, specialties,
// and the states they are licensed in. Used to pre-fill the job search filters.
const DEFAULT_FILTER_CREDENTIAL_FIELDS = [
  "healthcare_role",
  "specialty",
  "state_medical_licenses",
  "imlc_license",
] as const;

type CredentialFieldEntry<T> = { index: number; value: T | null };
type CredentialField<T> = { values?: CredentialFieldEntry<T>[] } | undefined;

type HealthcareRoleCredentialValue = { healthcare_role_id: string };
type SpecialtyCredentialValue = { specialty_code: string };
type StateLicenseCredentialValue = { state: string | null };
type ImlcCredentialValue = { state_codes: string[] };

const nonNullValues = <T>(field: CredentialField<T>): T[] =>
  (field?.values ?? [])
    .map((entry) => entry?.value)
    .filter((value): value is T => value !== null && value !== undefined);

export type ProviderDefaultJobFilters = {
  isLoading: boolean;
  professionalType: { id: string; name: string } | undefined;
  specialties: { id: string; name: string }[];
  stateCodes: string[];
};

/**
 * Derives sensible default job-search filters from the current provider's own
 * profile: their healthcare professional type, their specialties, and the
 * states they hold a license in (including IMLC states).
 */
export const useProviderDefaultJobFilters = (): ProviderDefaultJobFilters => {
  const trpc = useTRPC();

  // Omitting switchUserId resolves to the authenticated provider.
  const credentialsQuery = useQuery(
    trpc.credentialFiles.getCredentialsFields.queryOptions({
      fieldCodes: [...DEFAULT_FILTER_CREDENTIAL_FIELDS],
    }),
  );

  const credentials = credentialsQuery.data as
    | Record<string, unknown>
    | undefined;

  const professionalTypeId =
    (
      credentials?.[
        "healthcare_role"
      ] as CredentialField<HealthcareRoleCredentialValue>
    )?.values?.[0]?.value?.healthcare_role_id ?? undefined;

  const specialtyCodes = useMemo(
    () =>
      nonNullValues(
        credentials?.["specialty"] as CredentialField<SpecialtyCredentialValue>,
      )
        .map((value) => value.specialty_code)
        .filter((code): code is string => Boolean(code)),
    [credentials],
  );

  const stateCodes = useMemo(() => {
    const licensedStates = nonNullValues(
      credentials?.[
        "state_medical_licenses"
      ] as CredentialField<StateLicenseCredentialValue>,
    )
      .map((value) => value.state)
      .filter((state): state is string => Boolean(state));

    const imlcStates =
      (credentials?.["imlc_license"] as CredentialField<ImlcCredentialValue>)
        ?.values?.[0]?.value?.state_codes ?? [];

    return Array.from(new Set([...licensedStates, ...imlcStates]));
  }, [credentials]);

  const professionalTypeQuery = useQuery(
    trpc.healthcareProfessionalTypes.getById.queryOptions(
      professionalTypeId ? { id: professionalTypeId } : undefined,
      { enabled: Boolean(professionalTypeId) },
    ),
  );

  const specialtiesQuery = useQuery(
    trpc.healthcareProfessionalTypes.getByCodes.queryOptions(
      { codes: specialtyCodes },
      { enabled: specialtyCodes.length > 0 },
    ),
  );

  const professionalType = useMemo(() => {
    const data = professionalTypeQuery.data;
    return data ? { id: data.id, name: data.name } : undefined;
  }, [professionalTypeQuery.data]);

  const specialties = useMemo(
    () =>
      (specialtiesQuery.data ?? []).map((specialty) => ({
        id: specialty.id,
        name: specialty.name,
      })),
    [specialtiesQuery.data],
  );

  const isLoading =
    credentialsQuery.isLoading ||
    (Boolean(professionalTypeId) && professionalTypeQuery.isLoading) ||
    (specialtyCodes.length > 0 && specialtiesQuery.isLoading);

  return { isLoading, professionalType, specialties, stateCodes };
};
